import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import Typography from '@nubank/nuds-web/components/Typography/Typography';
import Link from '@nubank/nuds-web/components/Link/Link';

import Section from 'components/structure/Section';
import Block from 'components/structure/Block/Block';
import Carousel from 'components/Carousel/Carousel';
import base64Generate from '@nubank/www-latam-commons/utils/base64/generate';
import { paddingBuilder } from 'styles/themeUtils';

import { ReportsList } from './styles/ReportsList';

const FINANCIAL_STATEMENTS = {
  2017: {
    'FINANCIAL_STATEMENTS.FIRST.SEMESTER': [
      'FINANCIAL_STATEMENTS.CONSOLIDATED',
      'FINANCIAL_STATEMENTS.INDIVIDUAL.NU_PAGAMENTOS',
    ],

    'FINANCIAL_STATEMENTS.SECOND.SEMESTER': [
      'FINANCIAL_STATEMENTS.CONSOLIDATED',
      'FINANCIAL_STATEMENTS.INDIVIDUAL.NU_PAGAMENTOS',
    ],
  },

  2018: {
    'FINANCIAL_STATEMENTS.FIRST.SEMESTER': [
      'FINANCIAL_STATEMENTS.CONSOLIDATED',
      'FINANCIAL_STATEMENTS.INDIVIDUAL.NU_PAGAMENTOS',
    ],

    'FINANCIAL_STATEMENTS.SECOND.SEMESTER': [
      'FINANCIAL_STATEMENTS.CONSOLIDATED',
      'FINANCIAL_STATEMENTS.INDIVIDUAL.NU_PAGAMENTOS',
      'FINANCIAL_STATEMENTS.INDIVIDUAL.NU_FINANCEIRA',
    ],
  },

  2019: {
    'FINANCIAL_STATEMENTS.FIRST.SEMESTER': [
      'FINANCIAL_STATEMENTS.CONSOLIDATED',
      'FINANCIAL_STATEMENTS.INDIVIDUAL.NU_PAGAMENTOS',
      'FINANCIAL_STATEMENTS.INDIVIDUAL.NU_FINANCEIRA',
    ],

    'FINANCIAL_STATEMENTS.SECOND.SEMESTER': [
      'FINANCIAL_STATEMENTS.CONSOLIDATED',
      'FINANCIAL_STATEMENTS.INDIVIDUAL.NU_PAGAMENTOS',
      'FINANCIAL_STATEMENTS.INDIVIDUAL.NU_FINANCEIRA',
    ],
  },

  2020: {
    'FINANCIAL_STATEMENTS.FIRST.SEMESTER': [
      'FINANCIAL_STATEMENTS.CONSOLIDATED',
      'FINANCIAL_STATEMENTS.INDIVIDUAL.NU_PAGAMENTOS',
      'FINANCIAL_STATEMENTS.INDIVIDUAL.NU_FINANCEIRA',
    ],

    'FINANCIAL_STATEMENTS.SECOND.SEMESTER': [
      'FINANCIAL_STATEMENTS.CONSOLIDATED',
      'FINANCIAL_STATEMENTS.INDIVIDUAL.NU_PAGAMENTOS',
      'FINANCIAL_STATEMENTS.INDIVIDUAL.NU_FINANCEIRA',
    ],
  },
};

function SectionFinancialStatements({ financialStatements }) {
  const [currentStep, setCurrentStep] = useState(1);
  const intl = useIntl();

  const handleChangeCarouselStep = step => {
    setCurrentStep(step);
  };

  return (
    <>
      <Section minHeight="auto">
        <Block
          blockWidth="40%"
          justifyContent="start"
          padding={{
            default: paddingBuilder.vertical(0),
          }}
        >
          <Carousel
            currentStep={currentStep}
            onStepChanged={handleChangeCarouselStep}
            controlsColor="primary"
          >
            {Object.keys(financialStatements).reverse().map(year => (
              <Typography
                key={`year-${base64Generate()}`}
                color="black"
                colorVariant="default"
                tag="h2"
                gutterBottom={2}
                variant="supertitle"
              >
                {year}
              </Typography>
            ))}
          </Carousel>
        </Block>

        <Block blockWidth="60%" justifyContent="flex-start">
          <Carousel
            currentStep={currentStep}
            onStepChanged={handleChangeCarouselStep}
            showControls={false}
          >

            {Object.entries(financialStatements).reverse().map(([year, semesters]) => (
              <>
                {
                  Object.entries(semesters).map(([semesterKey, documents]) => (
                    <>
                      <Typography
                        intlKey={`${semesterKey}.TITTLE`}
                        gutterBottom={1}
                        variant="subtitle"
                        tag="h3"
                      />

                      <ReportsList>
                        {documents.map(financialStatementKey => {
                          const documentName = intl.formatMessage({ id: financialStatementKey });
                          const semester = intl.formatMessage({ id: semesterKey });

                          return (
                            <li key={`${financialStatementKey}.LABEL`}>
                              <Link
                                color="primary"
                                variant="action"
                                href={`/docs/financial_statements/${documentName}_${semester}_${year}.pdf`}
                                target="_blank"
                                intlKey={`${financialStatementKey}.LABEL`}
                              />
                            </li>
                          );
                        })}
                      </ReportsList>
                    </>
                  ))
                }
              </>
            ))}
          </Carousel>
        </Block>
      </Section>

      <Section minHeight="auto">
        <Block
          blockWidth="70%"
          justifyContent="start"
          padding={{
            default: paddingBuilder.vertical(0),
          }}
        >
          <Typography
            color="black"
            colorVariant="default"
            gutterBottom={2}
            intlKey="FINANCIAL_STATEMENTS.REPORTS.OTHER_REPORTS_TITLE"
            variant="title"
          />
          <ReportsList>
            <li key="FINANCIAL_STATEMENTS.REPORTS.RISK_MANAGEMENT">
              <Link
                color="primary"
                href="/docs/nubank-estrutura-de-gerenciamento-de-riscos.pdf"
                target="_blank"
                intlKey="FINANCIAL_STATEMENTS.REPORTS.RISK_MANAGEMENT"
                variant="action"
              />
            </li>
            <li key="FINANCIAL_STATEMENTS.REPORTS.PILAR3_REPORT.2020_1">
              <Link
                color="primary"
                href="/docs/estrutura-de-gerenciamento-de-riscos-dominio-publico-pilar3-q1-2020.pdf"
                target="_blank"
                intlKey="FINANCIAL_STATEMENTS.REPORTS.PILAR3_REPORT.2020_1"
                variant="action"
              />
            </li>
            <li key="FINANCIAL_STATEMENTS.REPORTS.PILAR3_REPORT.2020_4">
              <Link
                color="primary"
                href="/docs/estrutura-de-gerenciamento-de-riscos-dominio-publico-pilar3-q4-2020.pdf"
                target="_blank"
                intlKey="FINANCIAL_STATEMENTS.REPORTS.PILAR3_REPORT.2020_4"
                variant="action"
              />
            </li>
            <li key="FINANCIAL_STATEMENTS.REPORTS.PILAR3_ATTACHMENT.2020_2">
              <Link
                color="primary"
                href="https://cdn.nubank.com.br/p4rc/docs/anexo-pilar3-circular-3930.xlsx"
                target="_blank"
                intlKey="FINANCIAL_STATEMENTS.REPORTS.PILAR3_ATTACHMENT.2020_2"
                variant="action"
              />
            </li>
            <li key="FINANCIAL_STATEMENTS.REPORTS.PILAR3_ATTACHMENT.2020_4">
              <Link
                color="primary"
                href="/docs/anexo-pilar3-circular-3930-q4.xlsx"
                target="_blank"
                intlKey="FINANCIAL_STATEMENTS.REPORTS.PILAR3_ATTACHMENT.2020_4"
                variant="action"
              />
            </li>
            <li key="FINANCIAL_STATEMENTS.REPORTS.ACTION_PLAN_REPORT">
              <Link
                color="primary"
                href="/docs/plano-acao-tier.pdf"
                target="_blank"
                intlKey="FINANCIAL_STATEMENTS.REPORTS.ACTION_PLAN_REPORT"
                variant="action"
              />
            </li>
          </ReportsList>
        </Block>
      </Section>
    </>
  );
}

SectionFinancialStatements.defaultProps = {
  financialStatements: FINANCIAL_STATEMENTS,
};

SectionFinancialStatements.propTypes = {
  financialStatements: PropTypes.objectOf(PropTypes.object),
};

export default React.memo(SectionFinancialStatements);
